import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PartyCharts from "../components/PartyCharts";
import LedgerCrossCheck from "./LedgerCrossCheck";
import BankCrossCheck from "./BankCrossCheck";
import OpeningBalanceCrossCheck from "./OpeningBalanceCrossCheck";

function Summary() {
    // ============================================================
    // STATE
    // ============================================================

    const [partyData, setPartyData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedParty, setExpandedParty] = useState(null);

    // ============================================================
    // PARTY CONFIGURATION
    // ============================================================

    const partyTypes = [
        {
            key: "suppliers",
            title: "Suppliers",
            icon: "bi-box-seam",
            amountField: "supplier_amount",

            columns: [
                {
                    key: "name",
                    label: "Supplier",
                    type: "text"
                },
                {
                    key: "opening_balance",
                    label: "Opening Balance",
                    type: "currency"
                },
                {
                    key: "total_amount",
                    label: "Supplier Amount",
                    type: "currency"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "current_balance",
                    label: "Current Balance",
                    type: "currency"
                }
            ]
        },

        {
            key: "buyers",
            title: "Buyers",
            icon: "bi-person-check",
            amountField: "amount_received",

            columns: [
                {
                    key: "name",
                    label: "Buyer",
                    type: "text"
                },
                {
                    key: "opening_balance",
                    label: "Opening Balance",
                    type: "currency"
                },
                {
                    key: "total_amount",
                    label: "Buyer Amount",
                    type: "currency"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "current_balance",
                    label: "Current Balance",
                    type: "currency"
                }
            ]
        },

        {
            key: "banks",
            title: "Banks",
            icon: "bi-bank",

            columns: [
                {
                    key: "name",
                    label: "Bank",
                    type: "text"
                },
                {
                    key: "opening_balance",
                    label: "Ledger USD",
                    type: "usd"
                },
                {
                    key: "credit_usd",
                    label: "Credit USD",
                    type: "usd"
                },
                {
                    key: "credit_brl",
                    label: "Ledger BRL",
                    type: "brl"
                },
                {
                    key: "credit_brl",
                    label: "Credit BRL",
                    type: "brl"
                },
                {
                    key: "usd_difference",
                    label: "USD Difference",
                    type: "usd"
                },
                {
                    key: "brl_difference",
                    label: "BRL Difference",
                    type: "brl"
                }
            ]
        },

        {
            key: "transporters",
            title: "Transporters",
            icon: "bi-truck",
            amountField: "transportation_amount",

            columns: [
                {
                    key: "name",
                    label: "Transporter",
                    type: "text"
                },
                {
                    key: "opening_balance",
                    label: "Opening Balance",
                    type: "currency"
                },
                {
                    key: "total_amount",
                    label: "Transport Amount",
                    type: "currency"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "current_balance",
                    label: "Current Balance",
                    type: "currency"
                }
            ]
        },

        {
            key: "despatchos",
            title: "Despatchos",
            icon: "bi-send",
            amountField: "despatcho_amount",

            columns: [
                {
                    key: "name",
                    label: "Despatcho",
                    type: "text"
                },
                {
                    key: "opening_balance",
                    label: "Opening Balance",
                    type: "currency"
                },
                {
                    key: "total_amount",
                    label: "Despatcho Amount",
                    type: "currency"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "current_balance",
                    label: "Current Balance",
                    type: "currency"
                }
            ]
        },

        {
            key: "ports",
            title: "Ports",
            icon: "bi-geo-alt",

            columns: [
                {
                    key: "name",
                    label: "Port",
                    type: "text"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "total_amount",
                    label: "Total Amount",
                    type: "currency"
                }
            ]
        },

        {
            key: "shippings",
            title: "Shipping Lines",
            icon: "bi-water",

            columns: [
                {
                    key: "name",
                    label: "Shipping Line",
                    type: "text"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "total_amount",
                    label: "Total Amount",
                    type: "currency"
                }
            ]
        },

        {
            key: "redex_companies",
            title: "Redex Companies",
            icon: "bi-building",
            amountField: "redex_amount",

            columns: [
                {
                    key: "name",
                    label: "Redex Company",
                    type: "text"
                },
                {
                    key: "opening_balance",
                    label: "Opening Balance",
                    type: "currency"
                },
                {
                    key: "total_amount",
                    label: "Redex Amount",
                    type: "currency"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "current_balance",
                    label: "Current Balance",
                    type: "currency"
                }
            ]
        },

        {
            key: "others",
            title: "Others",
            icon: "bi-three-dots",

            columns: [
                {
                    key: "name",
                    label: "Party",
                    type: "text"
                },
                {
                    key: "opening_balance",
                    label: "Opening Balance",
                    type: "currency"
                },
                {
                    key: "total_amount",
                    label: "Total Amount",
                    type: "currency"
                },
                {
                    key: "ledger_count",
                    label: "Transactions",
                    type: "count"
                },
                {
                    key: "current_balance",
                    label: "Current Balance",
                    type: "currency"
                }
            ]
        }
    ];

    // ============================================================
    // FETCH ALL PARTY DATA
    // ============================================================

    useEffect(() => {
        fetchAllParties();
    }, []);

    const fetchAllParties = async () => {
        setLoading(true);
        setError("");

        try {
            const requests = partyTypes.map(async (partyType) => {
                try {
                    const response = await axios.get(
                        `https://eshaastra.in/api/parties/${partyType.key}`
                    );

                    return {
                        key: partyType.key,
                        data: response.data?.data || [],
                        success: true
                    };
                } catch (err) {
                    console.error(
                        `Error fetching ${partyType.key}:`,
                        err
                    );

                    return {
                        key: partyType.key,
                        data: [],
                        success: false
                    };
                }
            });

            const results = await Promise.all(requests);

            const formattedData = {};

            results.forEach((result) => {
                formattedData[result.key] = result.data;
            });

            setPartyData(formattedData);

        } catch (err) {
            console.error("Summary API Error:", err);

            setError(
                err.response?.data?.message ||
                "Unable to load party summary."
            );
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // FORMAT VALUES
    // ============================================================

    const formatValue = (value, type) => {
        const number = Number(value || 0);

        if (type === "currency") {
            return `₹${number.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
        }

        if (type === "usd") {
            return `$${number.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
        }

        if (type === "brl") {
            return `R$ ${number.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
        }

        if (type === "count") {
            return number.toLocaleString("en-IN");
        }

        if (type === "date") {
            if (!value) {
                return "N/A";
            }

            return new Date(value).toLocaleDateString("en-IN");
        }

        return value || "N/A";
    };

    // ============================================================
    // GET PARTY NAME
    // ============================================================

    const getPartyName = (party) => {
        return (
            party.name ||
            party.party_name ||
            party.title ||
            party.company_name ||
            party.bank_name ||
            party.supplier_name ||
            party.buyer_name ||
            "N/A"
        );
    };

    // ============================================================
    // TOGGLE PARTY
    // ============================================================

    const toggleParty = (partyKey, partyId) => {
        const id = `${partyKey}-${partyId}`;

        if (expandedParty === id) {
            setExpandedParty(null);
        } else {
            setExpandedParty(id);
        }
    };

    // ============================================================
    // OVERALL STATISTICS
    // ============================================================

    const statistics = useMemo(() => {
        let totalParties = 0;
        let totalTransactions = 0;
        let totalOpeningBalance = 0;
        let totalLedgerAmount = 0;

        Object.entries(partyData).forEach(([type, parties]) => {
            if (!Array.isArray(parties)) {
                return;
            }

            parties.forEach((party) => {
                totalParties++;

                totalTransactions +=
                    Number(party.ledger_count || 0);

                totalOpeningBalance +=
                    Number(party.opening_balance || 0);

                /*
                 * Bank amounts are currencies and should not be
                 * mixed with INR.
                 */
                if (type !== "banks") {
                    totalLedgerAmount +=
                        Number(party.total_amount || 0);
                }
            });
        });

        return {
            totalParties,
            totalTransactions,
            totalOpeningBalance,
            totalLedgerAmount
        };
    }, [partyData]);

    // ============================================================
    // PARTY SECTION
    // ============================================================

    const renderPartySection = (partyType) => {
        const parties = partyData[partyType.key] || [];

        return (
            <div
                className="card shadow-sm border-0 mb-4"
                key={partyType.key}
            >
                {/* ============================================
                    SECTION HEADER
                ============================================ */}

                <div className="card-header bg-white border-bottom py-3">

                    <div className="d-flex justify-content-between align-items-center">

                        <div className="d-flex align-items-center">

                            <div
                                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{
                                    width: "42px",
                                    height: "42px"
                                }}
                            >
                                <i
                                    className={`bi ${partyType.icon}`}
                                    style={{
                                        fontSize: "20px"
                                    }}
                                ></i>
                            </div>

                            <div>
                                <h5 className="mb-0 fw-bold">
                                    {partyType.title}
                                </h5>

                                <small className="text-muted">
                                    {parties.length}{" "}
                                    {parties.length === 1
                                        ? "party"
                                        : "parties"}
                                </small>
                            </div>

                        </div>

                        <span className="badge bg-primary">
                            {parties.length}
                        </span>

                    </div>

                </div>

                {/* ============================================
                    SECTION BODY
                ============================================ */}

                <div className="card-body p-0">

                    {parties.length === 0 ? (

                        <div className="text-center py-5 text-muted">

                            <i
                                className="bi bi-inbox"
                                style={{
                                    fontSize: "35px"
                                }}
                            ></i>

                            <div className="mt-2">
                                No {partyType.title.toLowerCase()} found.
                            </div>

                        </div>

                    ) : (

                        <div className="table-responsive">

                            <table className="table table-hover table-bordered align-middle mb-0">

                                {/* ====================================
                                    TABLE HEADER
                                ==================================== */}

                                <thead className="table-light">

                                    <tr>

                                        <th
                                            className="text-center"
                                            style={{
                                                width: "60px"
                                            }}
                                        >
                                            #
                                        </th>

                                        {partyType.columns.map(
                                            (column) => (
                                                <th
                                                    key={column.key}
                                                    className={
                                                        column.type ===
                                                        "currency" ||
                                                        column.type ===
                                                        "usd" ||
                                                        column.type ===
                                                        "brl" ||
                                                        column.type ===
                                                        "count"
                                                            ? "text-end"
                                                            : ""
                                                    }
                                                >
                                                    {column.label}
                                                </th>
                                            )
                                        )}

                                        <th
                                            className="text-center"
                                            style={{
                                                width: "110px"
                                            }}
                                        >
                                            Action
                                        </th>

                                    </tr>

                                </thead>

                                {/* ====================================
                                    TABLE BODY
                                ==================================== */}

                                <tbody>

                                    {parties.map(
                                        (party, index) => {

                                            const partyId =
                                                party._id ||
                                                party.party_id;

                                            const rowId =
                                                `${partyType.key}-${partyId}`;

                                            const isExpanded =
                                                expandedParty === rowId;

                                            return (
                                                <React.Fragment
                                                    key={rowId}
                                                >

                                                    {/* ====================
                                                        PARTY ROW
                                                    ==================== */}

                                                    <tr>

                                                        <td className="text-center">
                                                            {index + 1}
                                                        </td>

                                                        {partyType.columns.map(
                                                            (column) => {

                                                                let value =
                                                                    party[
                                                                        column.key
                                                                    ];

                                                                if (
                                                                    column.key ===
                                                                    "name"
                                                                ) {
                                                                    value =
                                                                        getPartyName(
                                                                            party
                                                                        );
                                                                }

                                                                return (
                                                                    <td
                                                                        key={
                                                                            column.key
                                                                        }
                                                                        className={
                                                                            column.type ===
                                                                                "currency" ||
                                                                            column.type ===
                                                                                "usd" ||
                                                                            column.type ===
                                                                                "brl" ||
                                                                            column.type ===
                                                                                "count"
                                                                                ? "text-end"
                                                                                : ""
                                                                        }
                                                                    >

                                                                        {column.key ===
                                                                        "name" ? (
                                                                            <strong>
                                                                                {
                                                                                    value
                                                                                }
                                                                            </strong>
                                                                        ) : (
                                                                            formatValue(
                                                                                value,
                                                                                column.type
                                                                            )
                                                                        )}

                                                                    </td>
                                                                );
                                                            }
                                                        )}

                                                        {/* ACTION */}

                                                        <td className="text-center">

                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${
                                                                    isExpanded
                                                                        ? "btn-primary"
                                                                        : "btn-outline-primary"
                                                                }`}
                                                                onClick={() =>
                                                                    toggleParty(
                                                                        partyType.key,
                                                                        partyId
                                                                    )
                                                                }
                                                            >

                                                                <i
                                                                    className={`bi ${
                                                                        isExpanded
                                                                            ? "bi-chevron-up"
                                                                            : "bi-chevron-down"
                                                                    }`}
                                                                ></i>

                                                                &nbsp;

                                                                {isExpanded
                                                                    ? "Hide"
                                                                    : "View"}

                                                            </button>

                                                        </td>

                                                    </tr>

                                                    {/* ====================
                                                        EXPANDED LEDGER ROW
                                                    ==================== */}

                                                    {isExpanded && (

                                                        <tr>

                                                            <td
                                                                colSpan={
                                                                    partyType
                                                                        .columns
                                                                        .length +
                                                                    2
                                                                }
                                                                className="bg-light p-3"
                                                            >

                                                                <div className="card border-0 shadow-sm">

                                                                    <div className="card-header bg-dark text-white">

                                                                        <div className="d-flex justify-content-between align-items-center">

                                                                            <strong>
                                                                                <i className="bi bi-journal-text me-2"></i>

                                                                                {
                                                                                    getPartyName(
                                                                                        party
                                                                                    )
                                                                                }{" "}
                                                                                - Ledger Details
                                                                            </strong>

                                                                            <span className="badge bg-light text-dark">
                                                                                {
                                                                                    party.ledger_count ||
                                                                                    0
                                                                                }{" "}
                                                                                Transactions
                                                                            </span>

                                                                        </div>

                                                                    </div>

                                                                    <div className="card-body p-0">

                                                                        {party.ledger_entries &&
                                                                        party
                                                                            .ledger_entries
                                                                            .length >
                                                                            0 ? (

                                                                            <div className="table-responsive">

                                                                                <table className="table table-sm table-bordered table-hover mb-0">

                                                                                    <thead className="table-secondary">

                                                                                        <tr>

                                                                                            <th>
                                                                                                #
                                                                                            </th>

                                                                                            <th>
                                                                                                Date
                                                                                            </th>

                                                                                            <th>
                                                                                                Type
                                                                                            </th>

                                                                                            <th>
                                                                                                Invoice
                                                                                            </th>

                                                                                            <th>
                                                                                                Booking No
                                                                                            </th>

                                                                                            <th>
                                                                                                Material
                                                                                            </th>

                                                                                            <th>
                                                                                                Container
                                                                                            </th>

                                                                                            <th className="text-end">
                                                                                                Amount
                                                                                            </th>

                                                                                            <th>
                                                                                                Currency
                                                                                            </th>

                                                                                        </tr>

                                                                                    </thead>

                                                                                    <tbody>

                                                                                        {party.ledger_entries.map(
                                                                                            (
                                                                                                ledger,
                                                                                                ledgerIndex
                                                                                            ) => {

                                                                                                /*
                                                                                                 * IMPORTANT:
                                                                                                 *
                                                                                                 * Do not use a generic
                                                                                                 * fallback such as:
                                                                                                 *
                                                                                                 * supplier_amount ||
                                                                                                 * amount_received ||
                                                                                                 * redex_amount
                                                                                                 *
                                                                                                 * because that can pick
                                                                                                 * the wrong party amount.
                                                                                                 *
                                                                                                 * Use the configured
                                                                                                 * amountField.
                                                                                                 */

                                                                                                const amount =
                                                                                                    partyType.amountField
                                                                                                        ? Number(
                                                                                                              ledger[
                                                                                                                  partyType
                                                                                                                      .amountField
                                                                                                              ] ||
                                                                                                                  0
                                                                                                          )
                                                                                                        : Number(
                                                                                                              ledger
                                                                                                                  .party
                                                                                                                  ?.party_amount ||
                                                                                                                  ledger.final_amount ||
                                                                                                                  0
                                                                                                          );

                                                                                                return (
                                                                                                    <tr
                                                                                                        key={
                                                                                                            ledger._id ||
                                                                                                            ledgerIndex
                                                                                                        }
                                                                                                    >

                                                                                                        <td>
                                                                                                            {ledgerIndex +
                                                                                                                1}
                                                                                                        </td>

                                                                                                        <td>
                                                                                                            {formatValue(
                                                                                                                ledger.date,
                                                                                                                "date"
                                                                                                            )}
                                                                                                        </td>

                                                                                                        <td>
                                                                                                            <span className="badge bg-secondary">
                                                                                                                {
                                                                                                                    ledger.type ||
                                                                                                                    "N/A"
                                                                                                                }
                                                                                                            </span>
                                                                                                        </td>

                                                                                                        <td>
                                                                                                            {
                                                                                                                ledger.invoice_no ||
                                                                                                                "N/A"
                                                                                                            }
                                                                                                        </td>

                                                                                                        <td>
                                                                                                            {
                                                                                                                ledger.booking_no ||
                                                                                                                "N/A"
                                                                                                            }
                                                                                                        </td>

                                                                                                        <td>
                                                                                                            {typeof ledger.material ===
                                                                                                            "object"
                                                                                                                ? ledger
                                                                                                                      .material
                                                                                                                      ?.name ||
                                                                                                                  "N/A"
                                                                                                                : ledger.material ||
                                                                                                                  "N/A"}
                                                                                                        </td>

                                                                                                        <td>
                                                                                                            {
                                                                                                                ledger.container_no ||
                                                                                                                "N/A"
                                                                                                            }
                                                                                                        </td>

                                                                                                        <td className="text-end fw-semibold">
                                                                                                            {formatValue(
                                                                                                                amount,
                                                                                                                "currency"
                                                                                                            )}
                                                                                                        </td>

                                                                                                        <td>
                                                                                                            {
                                                                                                                ledger.currency ||
                                                                                                                "N/A"
                                                                                                            }
                                                                                                        </td>

                                                                                                    </tr>
                                                                                                );
                                                                                            }
                                                                                        )}

                                                                                    </tbody>

                                                                                </table>

                                                                            </div>

                                                                        ) : (

                                                                            <div className="text-center text-muted py-4">

                                                                                <i className="bi bi-journal-x fs-3"></i>

                                                                                <div className="mt-2">
                                                                                    No ledger
                                                                                    transactions
                                                                                    found.
                                                                                </div>

                                                                            </div>

                                                                        )}

                                                                    </div>

                                                                </div>

                                                            </td>

                                                        </tr>

                                                    )}

                                                </React.Fragment>
                                            );
                                        }
                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </div>

            </div>
        );
    };

    // ============================================================
    // LOADING
    // ============================================================

    if (loading) {
        return (
            <div className="container-fluid py-5">

                <div className="text-center">

                    <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{
                            width: "3rem",
                            height: "3rem"
                        }}
                    ></div>

                    <div className="mt-3 text-muted">
                        Loading party summary...
                    </div>

                </div>

            </div>
        );
    }

    // ============================================================
    // ERROR
    // ============================================================

    if (error) {
        return (
            <div className="container-fluid py-4">

                <div className="alert alert-danger">

                    <div className="d-flex align-items-center">

                        <i className="bi bi-exclamation-triangle-fill me-2"></i>

                        <strong>
                            Error:
                        </strong>

                        <span className="ms-2">
                            {error}
                        </span>

                    </div>

                    <button
                        className="btn btn-danger btn-sm mt-3"
                        onClick={fetchAllParties}
                    >
                        <i className="bi bi-arrow-repeat me-1"></i>
                        Retry
                    </button>

                </div>

            </div>
        );
    }

    // ============================================================
    // MAIN UI
    // ============================================================

    return (
        <div className="container-fluid py-4">

            {/* ====================================================
                PAGE HEADER
            ==================================================== */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <div>

                    <h2 className="fw-bold mb-1">
                        <i className="bi bi-speedometer2 me-2 text-primary"></i>
                        Party Summary
                    </h2>

                    <p className="text-muted mb-0">
                        Overview of all parties, transactions and ledger balances.
                    </p>

                </div>

                <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={fetchAllParties}
                >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                </button>

            </div>

            {/* ====================================================
                SUMMARY CARDS
            ==================================================== */}

            <div className="row g-3 mb-4">

                {/* TOTAL PARTIES */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <div className="text-muted small">
                                        Total Parties
                                    </div>

                                    <h3 className="fw-bold mb-0">
                                        {statistics.totalParties.toLocaleString(
                                            "en-IN"
                                        )}
                                    </h3>

                                </div>

                                <div
                                    className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "50px",
                                        height: "50px"
                                    }}
                                >
                                    <i className="bi bi-people fs-4"></i>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

                {/* TRANSACTIONS */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <div className="text-muted small">
                                        Total Transactions
                                    </div>

                                    <h3 className="fw-bold mb-0">
                                        {statistics.totalTransactions.toLocaleString(
                                            "en-IN"
                                        )}
                                    </h3>

                                </div>

                                <div
                                    className="bg-success bg-opacity-10 text-success rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "50px",
                                        height: "50px"
                                    }}
                                >
                                    <i className="bi bi-journal-text fs-4"></i>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

                {/* OPENING BALANCE */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <div className="text-muted small">
                                        Opening Balance
                                    </div>

                                    <h3 className="fw-bold mb-0">
                                        {formatValue(
                                            statistics.totalOpeningBalance,
                                            "currency"
                                        )}
                                    </h3>

                                </div>

                                <div
                                    className="bg-warning bg-opacity-10 text-warning rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "50px",
                                        height: "50px"
                                    }}
                                >
                                    <i className="bi bi-wallet2 fs-4"></i>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

                {/* TOTAL LEDGER */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body">

                            <div className="d-flex justify-content-between align-items-center">

                                <div>

                                    <div className="text-muted small">
                                        Ledger Amount
                                    </div>

                                    <h3 className="fw-bold mb-0">
                                        {formatValue(
                                            statistics.totalLedgerAmount,
                                            "currency"
                                        )}
                                    </h3>

                                </div>

                                <div
                                    className="bg-info bg-opacity-10 text-info rounded-circle d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "50px",
                                        height: "50px"
                                    }}
                                >
                                    <i className="bi bi-cash-stack fs-4"></i>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

            {/* ====================================================
                PARTY CHARTS
            ==================================================== */}

            <div className="mb-4">

                <PartyCharts
                    partyData={partyData}
                    partyTypes={partyTypes}
                />

            </div>

            {/* ====================================================
                ALL PARTY SECTIONS
            ==================================================== */}

            {partyTypes.map((partyType) =>
                renderPartySection(partyType)
            )}

            {/* ====================================================
                LEDGER CROSS CHECK
            ==================================================== */}

            <div className="mt-4">

                <LedgerCrossCheck
        partyData={partyData}
        partyTypes={partyTypes}
    />
	
	<BankCrossCheck bankData={partyData.banks || []} />
	
	<OpeningBalanceCrossCheck
    partyData={partyData}
/>

            </div>

        </div>
    );
}

export default Summary;